import React from 'react';
import {
  ActivityIndicator,
  FlatList,
  Linking,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { COLORS } from '../config/colors';
import { FONTS } from '../config/fonts';
import { useIsHorizontal } from '../hooks/useIsHorizontal';
import { strings } from '../strings';
import MapButton from './MapButton';

export type ArticleListItem = {
  key: string;
  title: string;
  caption: string;
  url: string;
  // Publication time already formatted in the Jalali calendar, shown as a
  // dateline under the title (news list only).
  time?: string;
  // Short article description (news list only).
  description?: string;
};

type ArticleListProps = {
  title: string;
  items: ArticleListItem[];
  isLoading: boolean;
  error: string | null;
};

const ArticleList = ({ title, items, isLoading, error }: ArticleListProps) => {
  const insets = useSafeAreaInsets();
  const isHorizontal = useIsHorizontal();
  // A single column in portrait, two side-by-side columns in horizontal mode.
  const numColumns = isHorizontal ? 2 : 1;

  const handleOpen = (url: string) => {
    Linking.openURL(url).catch(caughtError => {
      console.error('Failed to open URL', url, caughtError);
    });
  };

  const renderItem = ({
    item,
    index,
  }: {
    item: ArticleListItem;
    index: number;
  }) => {
    const isEven = index % 2 === 0;

    return (
      <Pressable
        accessibilityHint={strings.opensInBrowser}
        accessibilityLabel={`${item.title}، ${item.caption}${
          item.time ? `، ${item.time}` : ''
        }`}
        accessibilityRole="link"
        onPress={() => handleOpen(item.url)}
        style={({ pressed }) => [
          styles.card,
          numColumns > 1 ? styles.cardColumn : styles.cardWide,
          isEven ? styles.cardEven : styles.cardOdd,
          pressed && styles.cardPressed,
        ]}
      >
        <View
          style={[styles.accent, isEven ? styles.accentEven : styles.accentOdd]}
        />
        <Text
          style={[
            styles.cardTitle,
            isEven ? styles.cardTitleEven : styles.cardTitleOdd,
          ]}
        >
          {item.title}
        </Text>
        {item.time ? (
          <Text
            style={[
              styles.cardTime,
              isEven ? styles.cardTimeEven : styles.cardTimeOdd,
            ]}
          >
            {item.time}
          </Text>
        ) : null}
        {item.description ? (
          <Text
            numberOfLines={3}
            style={[
              styles.cardDescription,
              isEven ? styles.cardDescriptionEven : styles.cardDescriptionOdd,
            ]}
          >
            {item.description}
          </Text>
        ) : null}
        <Text
          style={[
            styles.cardCaption,
            isEven ? styles.cardCaptionEven : styles.cardCaptionOdd,
          ]}
        >
          {item.caption}
        </Text>
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      <FlatList
        // numColumns cannot change on the fly: a new key forces a fresh list.
        key={numColumns}
        numColumns={numColumns}
        data={items}
        keyExtractor={item => item.key}
        renderItem={renderItem}
        ListHeaderComponent={
          <Text accessibilityRole="header" style={styles.title}>
            {title}
          </Text>
        }
        ListEmptyComponent={
          isLoading ? (
            <ActivityIndicator
              accessibilityLabel={strings.loading}
              accessibilityRole="progressbar"
              color={COLORS.primary}
              size="large"
              style={styles.state}
            />
          ) : error ? (
            <Text accessibilityRole="alert" style={styles.stateText}>
              {error}
            </Text>
          ) : null
        }
        columnWrapperStyle={numColumns > 1 ? styles.row : undefined}
        contentContainerStyle={[
          styles.listContent,
          { paddingTop: insets.top + 68, paddingBottom: insets.bottom + 24 },
        ]}
      />
      <MapButton />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.surface,
  },
  listContent: {
    paddingHorizontal: 16,
  },
  title: {
    fontFamily: FONTS.regular,
    fontSize: 26,
    color: COLORS.ink,
    textAlign: 'center',
    marginBottom: 20,
  },
  // Multi-column rows: keep the columns evenly spaced and centered.
  row: {
    width: '100%',
    justifyContent: 'center',
    gap: 14,
  },
  card: {
    direction: 'rtl',
    maxWidth: 640,
    borderRadius: 15,
    paddingHorizontal: 18,
    paddingVertical: 16,
    marginBottom: 14,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  // Single column: one full-width card per row, capped and centered by maxWidth.
  cardWide: {
    width: '100%',
    alignSelf: 'center',
  },
  // Multi-column: every card takes an equal share of the row.
  cardColumn: {
    flex: 1,
    flexBasis: 0,
  },
  cardEven: {
    backgroundColor: COLORS.articleEvenBackground,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(3, 4, 94, 0.12)',
  },
  cardOdd: {
    backgroundColor: COLORS.articleOddBackground,
  },
  cardPressed: {
    opacity: 0.9,
    transform: [{ scale: 0.99 }],
  },
  // Small colored ribbon at the top of every card.
  accent: {
    width: 40,
    height: 4,
    borderRadius: 2,
    marginBottom: 12,
  },
  accentEven: {
    backgroundColor: COLORS.primary,
  },
  accentOdd: {
    backgroundColor: '#90E0EF',
  },
  cardTitle: {
    fontFamily: FONTS.regular,
    fontSize: 18,
    lineHeight: 28,
    textAlign: 'justify',
  },
  cardTitleEven: {
    color: COLORS.ink,
  },
  cardTitleOdd: {
    color: '#ffffff',
  },
  // Jalali dateline shown right below the news title.
  cardTime: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    lineHeight: 18,
    marginTop: 6,
  },
  cardTimeEven: {
    color: 'rgba(3, 4, 94, 0.6)',
  },
  cardTimeOdd: {
    color: 'rgba(255, 255, 255, 0.75)',
  },
  // Short news description, clamped to a few lines.
  cardDescription: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    lineHeight: 24,
    textAlign: 'justify',
    marginTop: 8,
  },
  cardDescriptionEven: {
    color: 'rgba(3, 4, 94, 0.85)',
  },
  cardDescriptionOdd: {
    color: 'rgba(255, 255, 255, 0.92)',
  },
  cardCaption: {
    fontFamily: FONTS.regular,
    fontSize: 13,
    lineHeight: 20,
    textAlign: 'justify',
    marginTop: 8,
  },
  cardCaptionEven: {
    color: 'rgba(3, 4, 94, 0.65)',
  },
  cardCaptionOdd: {
    color: 'rgba(255, 255, 255, 0.85)',
  },
  state: {
    alignSelf: 'center',
    marginVertical: 48,
  },
  stateText: {
    fontFamily: FONTS.regular,
    fontSize: 15,
    lineHeight: 26,
    color: '#6b7280',
    textAlign: 'center',
    marginVertical: 48,
  },
});

export default ArticleList;
